<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import AudienceStep from './wizard/AudienceStep.vue';
import TemplateStep from './wizard/TemplateStep.vue';
import VariablesStep from './wizard/VariablesStep.vue';
import ScheduleStep from './wizard/ScheduleStep.vue';
import ReviewStep from './wizard/ReviewStep.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const STEPS = ['audience', 'template', 'variables', 'schedule', 'review'];
const stepIndex = ref(0);
const currentStep = computed(() => STEPS[stepIndex.value]);
const isSubmitting = ref(false);

const title = ref('');
const audienceConfig = ref({ mode: 'all_contacts' });
// Lifted here (rather than read back from AudienceStep via a template ref)
// because that step is v-if-unmounted once the wizard moves past it, and
// the Review step still needs to display the last known estimate.
const estimatedAudienceCount = ref(null);
const isAudienceConfigComplete = ref(false);
const inboxId = ref(null);
const selectedTemplate = ref(null);
const variables = ref(null);
const schedule = ref({ sendMode: 'now', scheduledAt: null });

const canGoNext = computed(() => {
  switch (currentStep.value) {
    case 'audience':
      return !!title.value.trim() && isAudienceConfigComplete.value;
    case 'template':
      return !!selectedTemplate.value;
    case 'variables':
      return !!variables.value?.isValid;
    case 'schedule':
      return schedule.value.sendMode === 'now' || !!schedule.value.scheduledAt;
    default:
      return true;
  }
});

const goNext = () => {
  if (!canGoNext.value) return;
  if (stepIndex.value < STEPS.length - 1) stepIndex.value += 1;
};
const goBack = () => {
  if (stepIndex.value > 0) stepIndex.value -= 1;
};

const resetWizard = () => {
  stepIndex.value = 0;
  title.value = '';
  audienceConfig.value = { mode: 'all_contacts' };
  estimatedAudienceCount.value = null;
  isAudienceConfigComplete.value = false;
  inboxId.value = null;
  selectedTemplate.value = null;
  variables.value = null;
  schedule.value = { sendMode: 'now', scheduledAt: null };
};

const handleClose = () => {
  emit('close');
};

// The frontend never sets campaign_status directly - create() relies
// entirely on the backend's default_campaign_status_for_whatsapp_v1
// (Campaign model) to start the campaign as `draft`, then schedule!/
// send_now! (via Campaigns::CampaignStateService) are what actually move
// it forward. Two sequential calls, matching the two separate backend
// actions - not one combined endpoint.
const confirmAndCreate = async () => {
  isSubmitting.value = true;
  try {
    const campaign = await store.dispatch('campaigns/create', {
      title: title.value,
      message: variables.value.renderedTemplate,
      template_params: variables.value.templateParams,
      inbox_id: inboxId.value,
      audience_config: audienceConfig.value,
    });

    if (schedule.value.sendMode === 'now') {
      await store.dispatch('campaigns/sendNow', campaign.id);
    } else {
      await store.dispatch('campaigns/schedule', {
        id: campaign.id,
        scheduledAt: new Date(schedule.value.scheduledAt).toISOString(),
      });
    }

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });
    useAlert(t('CAMPAIGN.WHATSAPP.WIZARD.CREATE_SUCCESS'));
    resetWizard();
    handleClose();
  } catch (error) {
    useAlert(error?.message || t('CAMPAIGN.WHATSAPP.WIZARD.CREATE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const open = () => {
  resetWizard();
  store.dispatch('inboxes/get');
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="resetWizard"
  >
    <div class="flex flex-col w-full gap-5">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.TITLE') }}
      </h3>

      <Input
        v-model="title"
        :label="t('CAMPAIGN.WHATSAPP.WIZARD.CAMPAIGN_NAME_LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.CAMPAIGN_NAME_PLACEHOLDER')"
      />

      <AudienceStep
        v-if="currentStep === 'audience'"
        v-model:audience-config="audienceConfig"
        v-model:estimated-count="estimatedAudienceCount"
        v-model:is-config-complete="isAudienceConfigComplete"
      />

      <TemplateStep
        v-else-if="currentStep === 'template'"
        v-model:inbox-id="inboxId"
        v-model:template="selectedTemplate"
      />

      <VariablesStep
        v-else-if="currentStep === 'variables'"
        v-model:variables="variables"
        :template="selectedTemplate"
      />

      <ScheduleStep
        v-else-if="currentStep === 'schedule'"
        v-model:schedule="schedule"
      />

      <ReviewStep
        v-else-if="currentStep === 'review'"
        :title="title"
        :audience-config="audienceConfig"
        :estimated-count="estimatedAudienceCount"
        :template="selectedTemplate"
        :rendered-template="variables?.renderedTemplate || ''"
        :schedule="schedule"
      />

      <div class="flex justify-between pt-2">
        <Button
          v-if="stepIndex > 0"
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.BACK')"
          variant="link"
          @click="goBack"
        />
        <span v-else />
        <Button
          v-if="currentStep !== 'review'"
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.NEXT')"
          :disabled="!canGoNext"
          @click="goNext"
        />
        <Button
          v-else
          :label="t('CAMPAIGN.WHATSAPP.WIZARD.CONFIRM_AND_CREATE')"
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          @click="confirmAndCreate"
        />
      </div>
    </div>
  </Dialog>
</template>
